/**
 * RTK Listener middleware for streaming animation.
 *
 * This module contains the listener that handles the streaming animation loop.
 * It listens for incoming chunks and animates them character-by-character,
 * updating the Redux state as it goes.
 *
 * Key responsibilities:
 * - Listen for messageChunkReceived actions
 * - Check per-message animation locks
 * - Process chunks sequentially
 * - Animate character-by-character with delays
 * - Update parser state and displayed text in Redux
 */

import { createListenerMiddleware, addListener, TypedStartListening, TypedAddListener } from '@reduxjs/toolkit';
import type { RootState, AppDispatch } from '../ReduxStore_Store';
import {
  messageChunkReceived,
  setAnimating,
  updateDisplayedText,
  updateParserState,
  removeChunk,
} from './streamingSlice';
import { parseCharacter } from './parser';
import type { ParserState } from './types';

// Type the listener middleware
export const streamingListenerMiddleware = createListenerMiddleware();

// Create typed versions of start/add listener
type AppStartListening = TypedStartListening<RootState, AppDispatch>;
type AppAddListener = TypedAddListener<RootState, AppDispatch>;

const startAppListening = streamingListenerMiddleware.startListening as AppStartListening;
const addAppListener = addListener as AppAddListener;

// Animation configuration
const CHAR_DELAY_MS = 20; // 20ms per character (50 chars/second)
const INSTANT_RENDER_WHEN_COMPLETE = true; // Skip animation delays when stream is complete
// Note: Removed CATCH_UP_THRESHOLD_MS - we now buffer smoothly and only skip delays when tab is hidden

/**
 * Delay helper for animation timing.
 *
 * @param ms - Milliseconds to delay
 * @returns Promise that resolves after the delay
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Check if the document is currently hidden (tab in background).
 * When hidden, browsers throttle setTimeout heavily.
 */
function isDocumentHidden(): boolean {
  return typeof document !== 'undefined' && document.hidden === true;
}

/**
 * Main animation listener.
 *
 * Listens for messageChunkReceived actions and processes chunks sequentially.
 *
 * Design Philosophy:
 * - SIMPLE: No manual lock checking - let RTK's predicate handle it
 * - RELIABLE: The while loop naturally picks up new chunks as they arrive
 * - ATOMIC: Redux guarantees state consistency
 *
 * How it works:
 * 1. Chunk arrives → messageChunkReceived dispatched
 * 2. Predicate checks: "Is this message already animating?"
 *    - YES: Listener doesn't start (chunk stays queued for current animation)
 *    - NO: Listener starts
 * 3. Set isAnimating = true
 * 4. While loop processes ALL chunks (including new ones that arrive)
 * 5. When queue truly empty, exit loop and release lock
 * 6. Next chunk arrival will start a fresh listener instance
 */
startAppListening({
  matcher: messageChunkReceived.match,
  effect: async (action, listenerApi) => {
    const { messageId, chunk, index } = action.payload;

    // Check if message is already animating
    const state = listenerApi.getState() as RootState;
    const message = state.streaming.messages[messageId];

    if (message?.isAnimating) {
      // Message already animating - chunk will be picked up by running loop
      return;
    }

    // Acquire animation lock
    listenerApi.dispatch(setAnimating({ messageId, isAnimating: true }));

    try {
      // Process all pending chunks sequentially
      // This loop keeps running as long as chunks exist in the queue
      while (true) {
        // Get FRESH state - crucial for picking up chunks that arrived during animation
        const currentState = listenerApi.getState() as RootState;
        const currentMessage = currentState.streaming.messages[messageId];

        if (!currentMessage) {
          console.error(`[LISTENER] Message ${messageId} disappeared during animation`);
          break;
        }

        // Check if any chunks remain
        if (currentMessage.pendingChunks.length === 0) {
          // CRITICAL: Yield to event loop to ensure any in-flight chunk dispatches complete
          // This prevents a race where a chunk arrives between our check and lock release
          await delay(0);

          // Double-check with fresh state
          const finalState = listenerApi.getState() as RootState;
          const finalMessage = finalState.streaming.messages[messageId];

          if (!finalMessage || finalMessage.pendingChunks.length === 0) {
            break; // Queue is empty, exit loop
          }

          // New chunks arrived during check, continue processing
          continue;
        }

        // Get current parser state
        let parserState: ParserState = currentMessage.parserState;

        // Check if stream is complete - if so, we batch-render all remaining chunks
        const shouldBatchRender = INSTANT_RENDER_WHEN_COMPLETE && currentMessage.isComplete;

        if (shouldBatchRender) {
          // BATCH MODE: Process all remaining chunks without intermediate Redux updates
          // This allows React to render everything at once with a smooth fade-in
          const chunksToProcess = [...currentMessage.pendingChunks];

          for (const chunk of chunksToProcess) {
            for (const char of chunk.text) {
              parserState = parseCharacter(char, parserState);
            }
          }

          // Single dispatch with final state - React renders once with CSS transition
          listenerApi.dispatch(
            updateParserState({
              messageId,
              parserState,
            })
          );
          listenerApi.dispatch(
            updateDisplayedText({
              messageId,
              displayedText: parserState.rendered,
            })
          );

          // Remove all processed chunks
          for (const chunk of chunksToProcess) {
            listenerApi.dispatch(
              removeChunk({
                messageId,
                chunkIndex: chunk.index,
              })
            );
          }

          // Exit the while loop - we've processed everything
          break;
        }

        // ANIMATED MODE: Process one chunk character-by-character
        const chunk = currentMessage.pendingChunks[0];

        // Animate this chunk character-by-character
        for (const char of chunk.text) {
          // Parse the character
          parserState = parseCharacter(char, parserState);

          // Update parser state in Redux
          listenerApi.dispatch(
            updateParserState({
              messageId,
              parserState,
            })
          );

          // Update displayed text in Redux
          listenerApi.dispatch(
            updateDisplayedText({
              messageId,
              displayedText: parserState.rendered,
            })
          );

          // Steady animation pace - only skip delay when tab is hidden
          // (browsers throttle setTimeout when tab is hidden, causing backlog)
          // Content will buffer in pendingChunks and animate smoothly when user returns
          if (!isDocumentHidden()) {
            await delay(CHAR_DELAY_MS);
          }
        }

        // Remove this chunk from the queue
        listenerApi.dispatch(
          removeChunk({
            messageId,
            chunkIndex: chunk.index,
          })
        );
      }
    } catch (error) {
      console.error(`[LISTENER] Error animating message ${messageId}:`, error);
    } finally {
      // Release animation lock
      listenerApi.dispatch(setAnimating({ messageId, isAnimating: false }));
    }
  },
});

/**
 * Export the middleware for registration in the store.
 */
export default streamingListenerMiddleware.middleware;
